import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Share2, Sparkles, Menu } from 'lucide-react';
import { ROUTES } from '../routes';
import { SearchBox } from './SearchBox';

interface TopBarProps {
  onQuickPreset: (salary: number) => void;
  onShare: () => void;
  onOpenMobileMenu: () => void;
}

const QUICK_PRESETS = [25000, 35000, 50000, 75000, 100000];

export const TopBar: React.FC<TopBarProps> = ({ onQuickPreset, onShare, onOpenMobileMenu }) => {
  return (
    <div className="bg-zinc-950 border-b border-zinc-800/80 sticky top-0 z-30 shadow-lg">
      <div className="px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
        {/* Mobile-only brand (sidebar carries this on desktop) */}
        <Link to={ROUTES.home} className="flex items-center gap-2 lg:hidden shrink-0">
          <div className="bg-gradient-to-tr from-emerald-500 to-teal-400 p-2 rounded-xl text-zinc-950 shrink-0">
            <Calculator className="w-4 h-4" />
          </div>
        </Link>

        {/* Quick Presets — desktop only */}
        <div className="hidden lg:flex items-center gap-1.5 shrink-0 text-xs">
          <span className="text-zinc-400 font-medium whitespace-nowrap flex items-center gap-1 text-[11px]">
            <Sparkles className="w-3 h-3 text-amber-400" />
            Quick:
          </span>
          {QUICK_PRESETS.map((sal) => (
            <button
              key={sal}
              onClick={() => onQuickPreset(sal)}
              className="px-2 py-0.5 text-[11px] rounded bg-zinc-900 hover:bg-emerald-500/20 hover:text-emerald-300 text-zinc-300 border border-zinc-800 transition-colors whitespace-nowrap font-mono"
            >
              £{(sal / 1000).toFixed(0)}k
            </button>
          ))}
        </div>

        {/* Search — grows to fill the row so Share/Menu sit flush right, capped
            so it doesn't stretch absurdly wide on ultra-wide monitors */}
        <div className="flex-1 min-w-0 max-w-2xl">
          <SearchBox variant="compact" placeholder="Search calculators & articles..." />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            onClick={onShare}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 transition-colors border border-zinc-800"
            title="Share or copy calculation link"
          >
            <Share2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="hidden sm:inline">Share</span>
          </button>

          <button
            onClick={onOpenMobileMenu}
            className="lg:hidden p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-800 transition-colors"
            aria-label="Open Navigation Menu"
          >
            <Menu className="w-5 h-5 text-emerald-400" />
          </button>
        </div>
      </div>

      {/* Quick Presets — mobile row below main bar */}
      <div className="lg:hidden flex items-center gap-1.5 overflow-x-auto px-4 pb-3 text-xs">
        <span className="text-zinc-400 font-medium whitespace-nowrap flex items-center gap-1 text-[11px]">
          <Sparkles className="w-3 h-3 text-amber-400" />
          Quick:
        </span>
        {QUICK_PRESETS.map((sal) => (
          <button
            key={sal}
            onClick={() => onQuickPreset(sal)}
            className="px-2 py-0.5 text-[11px] rounded bg-zinc-900 hover:bg-emerald-500/20 hover:text-emerald-300 text-zinc-300 border border-zinc-800 transition-colors whitespace-nowrap font-mono"
          >
            £{(sal / 1000).toFixed(0)}k
          </button>
        ))}
      </div>
    </div>
  );
};
